import { fmtDurationMs, testResultLabel, testResultPillClass, type TestDetail } from "../lib/api.ts";

// Type-aware test-parameter table. Mirrors the on-device report
// (btquick/AS1851_tmpl.html + btstatus.cpp genPdfReport) and the cloud PDF
// (apps/api/src/reports.ts buildParamRows) so the web view, the cloud PDF and
// the printed device report all show the same rows + precision per test type.
// Keys not consumed by the known-type layout fall through to an "Other
// parameters" passthrough, so new device params surface automatically.

const TEST_TYPE_AS1851 = 1;
const TEST_TYPE_CCD    = 2;
const TEST_TYPE_CPD    = 3;
const TEST_TYPE_BIR    = 4;
const TEST_TYPE_ECAP   = 6;

function num(v: unknown): number | null {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function fmt(v: unknown, decimals: number, unit: string): string {
  const n = num(v);
  return n == null ? "—" : `${n.toFixed(decimals)}${unit}`;
}

// "Other parameters" cosmetics: snake_case keys read as labels, long floats
// rounded — the raw dump ("delivered_ah 167.677461329386") read badly.
function prettyKey(k: string): string {
  const t = k.replace(/_/g, " ");
  return t.charAt(0).toUpperCase() + t.slice(1);
}
function prettyVal(v: unknown): string {
  if (typeof v === "number" && Number.isFinite(v)) {
    return Number.isInteger(v) ? String(v) : String(Math.round(v * 1000) / 1000);
  }
  if (typeof v === "string" && v.trim() !== "" && Number.isFinite(Number(v))) {
    const n = Number(v);
    return Number.isInteger(n) ? String(n) : String(Math.round(n * 1000) / 1000);
  }
  return String(v);
}

// Capacity-rate + delivered-capacity rows (CCD/CPD, phase-1 capacity feature). Only
// present on tests recorded with the feature; absent → rows skipped so old tests stay
// clean. Mirrors reports.ts capacityRows + the on-device report.
function capacityRows(params: Record<string, unknown>): Array<[string, string]> {
  const out: Array<[string, string]> = [];
  const rateH = num(params.capacity_rate_h);
  if (rateH != null) out.push(["Capacity type", `C${rateH}`]);
  const passPct = num(params.passed_capacity_pct);
  if (passPct != null) out.push(["Passed capacity", `${passPct}%`]);
  const delivered = num(params.delivered_ah);
  if (delivered != null) {
    const rated = num(params.capacity);
    const pct = rated != null && rated > 0 ? ` (${Math.round(100 * delivered / rated)}% of rated)` : "";
    out.push(["Delivered capacity", `${delivered.toFixed(1)} Ah${pct}`]);
  }
  return out;
}

// Labelled rows per test type, mirroring reports.ts buildParamRows. `known`
// collects every param key the layout consumes (incl. ones intentionally not
// shown, like AS1851's standard-fixed stop voltage) so they don't also appear
// under "Other parameters".
function buildRows(
  test: TestDetail, params: Record<string, unknown>,
): { rows: Array<[string, string]>; known: Set<string> } {
  const rows: Array<[string, string]> = [];
  const known = new Set<string>();
  const use = (...keys: string[]) => { for (const k of keys) known.add(k); };

  const q = num(params.q_current);
  const qText = q != null && q > 0 ? `${q.toFixed(1)}A` : "Not used";

  // §0.4 battery descriptor — maker/model as entered on the device (sentinels
  // "Unknown" / "Power Supply" render as-is). Shared by every test type.
  use("battery_maker", "battery_model");
  // Ride-along signature + charger-detection params are analytics data, not
  // customer rows (the device PDF dropped them too, 2026-08-13) — consume
  // them so they don't dump under "Other parameters". The bench-only
  // standalone pulse type (7) keeps the dump: there they ARE the content.
  if (test.testType !== 7) {
    for (const k of Object.keys(params))
      if (k.startsWith("pulse_") || k.startsWith("charger_")) known.add(k);
  }
  const bMaker = typeof params.battery_maker === "string" ? params.battery_maker.trim() : "";
  const bModel = typeof params.battery_model === "string" ? params.battery_model.trim() : "";
  if (bMaker) rows.push(["Battery", bModel ? `${bMaker} ${bModel}` : bMaker]);

  if (test.testType === TEST_TYPE_AS1851) {
    // AS1851 stop voltage is fixed by the standard (0.875×nominal) — implied,
    // not shown — matching the device report. Consume the key so it doesn't
    // leak into "Other parameters".
    use("capacity", "start_voltage", "end_voltage", "q_current", "current", "stop_voltage");
    rows.push(["Battery Capacity",         fmt(params.capacity, 0, "Ah")]);
    rows.push(["Battery Voltage at Start", fmt(params.start_voltage, 1, "V")]);
    rows.push(["Battery Voltage at End",   fmt(params.end_voltage, 1, "V")]);
    rows.push(["Quiescent Current",        qText]);
    rows.push(["Discharge Current",        fmt(params.current, 1, "A")]);
  } else if (test.testType === TEST_TYPE_CCD) {
    use("capacity", "start_voltage", "stop_voltage", "end_voltage", "current",
        "capacity_rate_h", "delivered_ah", "passed_capacity_pct");
    rows.push(["Battery Capacity",         fmt(params.capacity, 0, "Ah")]);
    rows.push(["Battery Voltage at Start", fmt(params.start_voltage, 1, "V")]);
    rows.push(["Stop Voltage",             fmt(params.stop_voltage, 1, "V")]);
    rows.push(["Battery Voltage at End",   fmt(params.end_voltage, 1, "V")]);
    rows.push(["Quiescent Current",        "Not used"]);
    rows.push(["Discharge Current",        fmt(params.current, 1, "A")]);
    rows.push(...capacityRows(params));
  } else if (test.testType === TEST_TYPE_CPD) {
    // CPD is constant-power: device stores `power`, report shows a Discharge
    // Power row in place of discharge current.
    use("capacity", "power", "target_power", "set_power",
        "start_voltage", "stop_voltage", "end_voltage",
        "capacity_rate_h", "delivered_ah", "passed_capacity_pct");
    const power = params.power ?? params.target_power ?? params.set_power;
    rows.push(["Battery Capacity",         fmt(params.capacity, 0, "Ah")]);
    rows.push(["Battery Voltage at Start", fmt(params.start_voltage, 1, "V")]);
    rows.push(["Stop Voltage",             fmt(params.stop_voltage, 1, "V")]);
    rows.push(["Battery Voltage at End",   fmt(params.end_voltage, 1, "V")]);
    rows.push(["Quiescent Current",        "Not used"]);
    rows.push(["Discharge Power",          fmt(power, 0, "W")]);
    rows.push(...capacityRows(params));
  } else if (test.testType === TEST_TYPE_BIR) {
    // BIR (internal resistance): two-level discharge. Headline R (mΩ) + the two
    // operating points (V/I per level) — mirrors reports.ts buildParamRows.
    // start_voltage is consumed (open-circuit) but not shown, to keep the table focused.
    use("resistance_mohm", "capacity", "v1", "v2", "i1", "i2", "start_voltage");
    rows.push(["Internal Resistance",         fmt(params.resistance_mohm, 2, "mΩ")]);
    rows.push(["Battery Capacity",            fmt(params.capacity, 0, "Ah")]);
    rows.push(["Level 1 — Voltage / Current", `${fmt(params.v1, 2, "V")} / ${fmt(params.i1, 1, "A")}`]);
    rows.push(["Level 2 — Voltage / Current", `${fmt(params.v2, 2, "V")} / ${fmt(params.i2, 1, "A")}`]);
  } else if (test.testType === TEST_TYPE_ECAP) {
    // ECAP (estimated standby capacity): computed C20 + quiescent/alarm currents
    // (mA — small) and durations; the chart is the clamp-current trace.
    use("required_capacity", "q_current", "alarm_current", "q_hours", "alarm_hours", "measured");
    rows.push(["Required Capacity",  fmt(params.required_capacity, 0, "Ah")]);
    rows.push(["Quiescent Current",  params.q_current == null ? "—" : fmt((params.q_current as number) * 1000, 0, "mA")]);
    rows.push(["Alarm Current",      params.alarm_current == null ? "—" : fmt((params.alarm_current as number) * 1000, 0, "mA")]);
    rows.push(["Quiescent Duration", fmt(params.q_hours, 0, "h")]);
    rows.push(["Alarm Duration",     fmt(params.alarm_hours, 2, "h")]);
    rows.push(["Source",             params.measured ? "Measured" : "Manual"]);
  }

  // End/abort snapshot (device recordTripContext): only the reason is
  // user-facing. The numeric set/measured/battery snapshot stays internal
  // (params_json + staff analytics) — consumed here so it never leaks into
  // "Other parameters".
  use("trip_reason", "trip_set_a", "trip_measured_a", "trip_voltage_v", "trip_stamp");
  if (params.trip_reason != null) {
    rows.push(["End Reason", String(params.trip_reason)]);
  }
  return { rows, known };
}

export function TestParameters({
  test, params,
}: {
  test:   TestDetail;
  params: Record<string, unknown>;
}) {
  const { rows, known } = buildRows(test, params);
  const haveLayout = rows.length > 0;
  const extras = Object.entries(params).filter(([k]) => !known.has(k));

  return (
    <>
      <table>
        <tbody>
          <tr>
            <td>Test Result</td>
            <td>
              <span className={testResultPillClass(test.testResult)}>
                {testResultLabel(test.testResult)}
              </span>
            </td>
          </tr>
          {test.testType !== TEST_TYPE_ECAP && (
            <tr>
              <td>Test Duration</td>
              <td>{fmtDurationMs(test.durationMs)}</td>
            </tr>
          )}
          {test.combinedRole === 1 && (
            <tr>
              <td>Combined</td>
              <td>{test.peerSerial ? `2× (with ${test.peerSerial})` : "2× (two units)"}</td>
            </tr>
          )}
          {rows.map(([label, val]) => (
            <tr key={label}>
              <td>{label}</td>
              <td>{val}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {extras.length > 0 && (
        <>
          <h4 style={{ margin: "1rem 0 0.5rem" }}>
            {haveLayout ? "Other parameters" : "Parameters"}
          </h4>
          <table>
            <tbody>
              {extras.map(([k, v]) => (
                <tr key={k}>
                  <td>{prettyKey(k)}</td>
                  <td>{prettyVal(v)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </>
  );
}
